import React, { useState } from "react";
import customisationImages from "../assets/customisationImages";
import { Button } from "./common/Button"; // 1. Import Button

// ... (logic for getOptionLabel, getStepOptions, etc. is unchanged)
// Steps that use multi-select
const multiSelectSteps = new Set(["addons", "sides", "specials"]);

type MenuOption = { name: string; price?: number } | string | undefined;

type MenuItem = {
  Variants?: { name: string; price?: number }[];
  Sizes?: { name: string; price?: number }[];
  Addons?: MenuOption[];
  Sides?: MenuOption[];
  Specials?: MenuOption[];
  photoUrl?: string;
  name?: string;
  Base?: string;
  price?: number;
  cafeName?: string;
  description?: string;
  [key: string]: any;
};

type CustomiseStepperProps = {
  item: MenuItem;
  onDone: (selections: Record<string, string[] | string>, quantity: number) => void;
  onClose: () => void; // 2. Add onClose prop
};

const getOptionLabel = (option: MenuOption): string =>
  typeof option === "string" ? option : (option?.name ?? "");

const getStepOptions = (item: MenuItem, step: string): string[] | null => {
  switch (step) {
    case "variant":
      return item.Variants ? item.Variants.map(v => v.name) : null;
    case "size":
      return item.Sizes ? item.Sizes.map(s => s.name) : null;
    case "addons":
      return item.Addons ? item.Addons.filter(Boolean).map(getOptionLabel) : null;
    case "sides":
      return item.Sides ? item.Sides.filter(Boolean).map(getOptionLabel) : null;
    case "specials":
      return item.Specials ? item.Specials.filter(Boolean).map(getOptionLabel) : null;
    default:
      return null;
  }
};

const getRelevantSteps = (item: MenuItem): string[] => {
  const steps: string[] = [];
  if (item.Variants) steps.push("variant");
  if (item.Sizes) steps.push("size");
  if (item.Addons) steps.push("addons");
  if (item.Sides) steps.push("sides");
  if (item.Specials) steps.push("specials");
  return steps;
};

const stepLabels: Record<string, string> = {
  variant: "Choose Variant",
  size: "Choose Size",
  addons: "Pick Add-ons",
  sides: "Pick Sides",
  specials: "Pick Specials",
};


const CustomiseStepper: React.FC<CustomiseStepperProps> = ({ item, onDone, onClose }) => {
  const steps = getRelevantSteps(item);
  const [currentStepIdx, setStepIdx] = useState(0);
  const [selections, setSelections] = useState<Record<string, string[] | string>>({});
  const [quantity, setQuantity] = useState(1);

  const currentStep = steps[currentStepIdx];
  const options = getStepOptions(item, currentStep);

  const handleOptionClick = (option: string) => {
    if (multiSelectSteps.has(currentStep)) {
      const selected = (selections[currentStep] as string[] | undefined) || [];
      setSelections({
        ...selections,
        [currentStep]: selected.includes(option)
          ? selected.filter(o => o !== option)
          : [...selected, option]
      });
    } else {
      setSelections({ ...selections, [currentStep]: option });
      if (currentStepIdx < steps.length - 1) setStepIdx(currentStepIdx + 1);
    }
  };

  const handleBack = () => setStepIdx(Math.max(currentStepIdx - 1, 0));
  
  const handleDone = () => {
    onDone(selections, quantity);
  };

  // --- Preview Image Logic ---
  let preview: string | undefined;
  if (multiSelectSteps.has(currentStep)) {
    const selected = selections[currentStep] as string[] | undefined;
    if (selected?.length) preview = customisationImages[selected[selected.length - 1]];
  }
  const rawSelection = selections[currentStep];
  const currentSelection: string | undefined = Array.isArray(rawSelection)
    ? rawSelection[0]
    : rawSelection;
  const safeVariant: string | undefined = Array.isArray(selections.variant)
    ? selections.variant[0]
    : selections.variant;
  const safeName: string | undefined = Array.isArray(item.name)
    ? item.name[0]
    : item.name;
  preview =
    preview ||
    customisationImages[getOptionLabel(currentSelection)] ||
    customisationImages[getOptionLabel(safeVariant)] ||
    item.photoUrl ||
    customisationImages[getOptionLabel(safeName)] ||
    "/food-hero.svg";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-lg max-w-xl w-full shadow-lg relative max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500 text-2xl"
          onClick={onClose} // 3. Use onClose prop
        >
          ✕
        </button>
        <img
          src={preview}
          alt="Current Dish Preview"
          className="w-full h-48 object-cover rounded mb-4"
        />
        <h3 className="text-2xl font-bold mb-3">
          {stepLabels[currentStep]}
        </h3>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {options &&
            options.map(option => {
              const isSelected = multiSelectSteps.has(currentStep)
                ? Array.isArray(selections[currentStep]) &&
                  (selections[currentStep] as string[]).includes(option)
                : selections[currentStep] === option;

              return (
                <button
                  key={option}
                  className={`flex flex-col items-center py-2 px-2 rounded border ${
                    isSelected
                      ? "border-indigo-600 bg-indigo-50"
                      : "border-gray-300"
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  <img
                    src={customisationImages[option] || "/default-customisation.jpg"}
                    alt={option}
                    className="h-16 w-16 rounded object-cover mb-2"
                  />
                  <span className="font-medium">{option}</span>
                  {multiSelectSteps.has(currentStep) && (
                    <span className="mt-1 text-xs">
                      {isSelected ? "✓ Selected" : ""}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
        
        <div className="my-4 flex items-center gap-3">
          <span className="font-medium">Quantity:</span>
          <Button
            variant="secondary"
            className="px-3 py-1"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            disabled={quantity === 1}
          >
            –
          </Button>
          <span className="px-3 font-bold text-lg">{quantity}</span>
          <Button
            variant="secondary"
            className="px-3 py-1"
            onClick={() => setQuantity(quantity + 1)}
          >
            +
          </Button>
        </div>

        <div className="flex justify-between">
          <Button
            onClick={handleBack}
            variant="secondary"
            className={`px-4 py-2 ${
              currentStepIdx === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={currentStepIdx === 0}
          >
            Back
          </Button>
          {multiSelectSteps.has(currentStep) ? (
            <Button
              onClick={() => setStepIdx(Math.min(currentStepIdx + 1, steps.length - 1))}
              variant="primary"
              className="px-4 py-2"
              disabled={
                !Array.isArray(selections[currentStep]) ||
                (selections[currentStep] as string[]).length === 0
              }
            >
              Next
            </Button>
          ) : currentStepIdx === steps.length - 1 ? (
            <Button
              onClick={handleDone} // 4. Call handleDone
              variant="primary"
              className="px-4 py-2 bg-green-600 hover:bg-green-700"
            >
              Add to Cart
            </Button>
          ) : (
            // 5. Add a "Next" button for single-select steps
            <Button
              onClick={() => setStepIdx(currentStepIdx + 1)}
              variant="primary"
              className="px-4 py-2"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomiseStepper;